import { routes } from '../../../common/contants/Routes';
import { isGuideDisabled } from '../helpers/guideDisableHelper';
import {
  applyHostColorSchemeToIframeElement,
  getHostColorScheme,
  startHostColorSchemeObserver
} from '../helpers/hostColorSchemeHelper';
import { MessageTypes } from '../types/MessageTypes';

/**
 * Singleton iframe hosting the hotspot tooltip (`/hs/:companyId`).
 *
 * Sits below `Overlay` (2147483645) and `Container` (2147483647) so a running
 * guide always paints over a hotspot tooltip — hotspots are the passive layer.
 */
class HotspotContainer {
  private static instance: HotspotContainer | null = null;
  private container: HTMLIFrameElement | null = null;
  private companyId: string | null = null;
  private fadeOutTimeout: ReturnType<typeof setTimeout> | null = null;
  private stopColorSchemeObserver: (() => void) | null = null;

  private constructor() {
    this.initialize();
  }

  public static getInstance(): HotspotContainer {
    if (!HotspotContainer.instance) {
      HotspotContainer.instance = new HotspotContainer();
    }
    return HotspotContainer.instance;
  }

  private initialize(): void {
    const scriptElement = document.querySelector('script[gd-company-id]');
    this.companyId = scriptElement?.getAttribute('gd-company-id') || null;
    this.container = document.createElement('iframe');

    const baseUrl = import.meta.env.VITE_TOUR_GUIDE_FRONTEND_URL;
    const iframeUrl = new URL(`${baseUrl}${routes.HOTSPOT_PLAYER}/${this.companyId}`);

    const parentParams = new URLSearchParams(window.location.search);
    if (parentParams.get('guideLog') === 'true') {
      iframeUrl.searchParams.set('guideLog', 'true');
    }
    if (isGuideDisabled()) {
      iframeUrl.searchParams.set('guideDisable', 'true');
    }
    const hostColorScheme = getHostColorScheme();
    iframeUrl.searchParams.set('hostColorScheme', hostColorScheme);

    this.container.src = iframeUrl.toString();
    applyHostColorSchemeToIframeElement(this.container, hostColorScheme);
    this.container.toggleAttribute('getdemo-hotspot-player', true);
    this.container.style.setProperty('position', 'fixed');
    this.container.style.setProperty('border', 'none');
    this.container.style.setProperty('background-color', 'transparent');
    this.container.style.setProperty('z-index', '2147483644');
    this.container.style.setProperty('width', '0');
    this.container.style.setProperty('height', '0');
    this.container.style.setProperty('top', '0');
    this.container.style.setProperty('left', '0');
    this.container.style.setProperty('opacity', '0');
    this.container.style.setProperty('display', 'none');
    this.container.style.setProperty('overflow', 'hidden');

    this.setupListeners();
    this.stopColorSchemeObserver = startHostColorSchemeObserver(scheme =>
      this.syncHostColorScheme(scheme)
    );
    document.documentElement.appendChild(this.container);
  }

  private syncHostColorScheme(hostColorScheme: string): void {
    applyHostColorSchemeToIframeElement(this.container, hostColorScheme);
    this.sendMessage({ type: MessageTypes.HOST_COLOR_SCHEME_CHANGED, hostColorScheme });
  }

  private setupListeners(): void {
    if (!this.container) return;

    this.container.addEventListener('load', () => {
      this.sendSavedUserData();
      this.syncHostColorScheme(getHostColorScheme());
    });
  }

  private sendSavedUserData(): void {
    const savedUserData = localStorage.getItem('getdemo_user');
    if (!savedUserData) return;
    try {
      this.sendMessage({
        type: MessageTypes.IDENTIFY_USER,
        userData: JSON.parse(savedUserData)
      });
    } catch (error) {
      console.error('Error processing saved user data:', error);
    }
  }

  public sendMessage(message: any): void {
    this.container?.contentWindow?.postMessage(message, '*');
  }

  public getContainer(): HTMLIFrameElement | null {
    return this.container;
  }

  public resizeAndPosition(
    position: { top: string; left: string },
    dimensions: { width: string; height: string }
  ): void {
    if (!this.container) return;
    this.container.style.setProperty('top', position.top);
    this.container.style.setProperty('left', position.left);
    this.container.style.setProperty('width', dimensions.width);
    this.container.style.setProperty('height', dimensions.height);
  }

  public show(): void {
    if (!this.container) return;
    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout);
      this.fadeOutTimeout = null;
    }
    this.container.style.setProperty('display', 'block');
    this.container.style.setProperty('opacity', '1');
  }

  public hide(): void {
    if (!this.container) return;
    if (this.fadeOutTimeout) clearTimeout(this.fadeOutTimeout);

    this.container.style.setProperty('opacity', '0');
    this.fadeOutTimeout = setTimeout(() => {
      this.container?.style.setProperty('display', 'none');
      this.fadeOutTimeout = null;
    }, 200);
  }

  public destroy(): void {
    if (this.stopColorSchemeObserver) {
      this.stopColorSchemeObserver();
      this.stopColorSchemeObserver = null;
    }
    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout);
      this.fadeOutTimeout = null;
    }
    if (this.container && document.documentElement.contains(this.container)) {
      document.documentElement.removeChild(this.container);
    }
    this.container = null;
    HotspotContainer.instance = null;
  }

  public static destroy(): void {
    HotspotContainer.instance?.destroy();
  }
}

export default HotspotContainer;
